"use client";

import { Link } from "@/src/lib/next-compat";
import { Check, ArrowRight, CheckCircle } from "lucide-react";
import { useState } from "react";
import { useSearchParams } from "@/src/lib/next-compat";

export interface PricingCard {
  key: string;
  courseTitle: string;
  /** Null when the course has no active Level 1 plan to sell. */
  planId: number | null;
  priceFormatted: string | null;
  alreadyOwned: boolean;
}

interface PricingPlansProps {
  /** Null while loading. */
  cards: PricingCard[] | null;
}

/** What a Level 1 purchase actually gives in V2 — no platform-wide access is implied. */
const featuresFor = (courseTitle: string) => [
  `Lifetime access to Level 1 of ${courseTitle}`,
  "Earn a certificate when you complete the level",
  "Higher levels available from the course page",
  "One-time payment, no subscription",
];

const FAQ = [
  {
    q: "What do I get when I buy a course here?",
    a: "Level 1 of the course you choose, as a one-time purchase that stays yours. Tax for your billing country is added at checkout.",
  },
  {
    q: "Can I buy the higher levels?",
    a: "Yes, from the course page. Which levels you can buy depends on your placement assessment and the levels you have already completed.",
  },
  {
    q: "Is this a subscription?",
    a: "No. It is a single payment with no recurring charge.",
  },
  {
    q: "How can I pay?",
    a: "By card through HyperPay for billing addresses in the Gulf, and by card, mobile wallet or bank account through PayFast for addresses in Pakistan.",
  },
];

export default function PricingPlans({ cards }: PricingPlansProps) {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const searchParams = useSearchParams();
  const switched = searchParams.get("switched") === "1";

  return (
    <div>
      {/* Switch banner */}
      {switched && (
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-green-200 bg-green-50 px-4 py-3">
          <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
          <p className="text-sm text-green-800">
            <span className="font-semibold">Your previous plan has been cancelled.</span> Choose a new plan below to continue your learning.
          </p>
        </div>
      )}

      {/* One card per course, each selling its Level 1 */}
      <div className="grid gap-8 sm:grid-cols-2 mb-12 mt-4">
        {(cards ?? [null, null]).map((card, i) => (
          <div
            key={card?.key ?? i}
            className="flex flex-col rounded-2xl border border-gray-200 bg-white p-7 shadow-sm"
          >
            <p className="mb-1 text-xs font-bold uppercase tracking-widest text-indigo-400">
              {card?.courseTitle ?? "\u00a0"}
            </p>
            <div className="mb-1 flex items-end gap-2">
              <span className="text-4xl font-bold text-gray-900">{card?.priceFormatted ?? "—"}</span>
              <span className="mb-1 text-sm text-gray-400">/ Level 1</span>
            </div>
            <p className="mb-6 text-sm text-gray-500">One-time payment · yours to keep</p>

            <ul className="mb-8 flex-1 space-y-3">
              {(card ? featuresFor(card.courseTitle) : []).map((text) => (
                <li key={text} className="flex items-center gap-3 text-sm text-gray-600">
                  <Check className="h-4 w-4 shrink-0 text-indigo-500" />
                  {text}
                </li>
              ))}
            </ul>

            {card?.alreadyOwned ? (
              <button disabled className="w-full cursor-default rounded-xl border border-green-200 bg-green-50 py-3 text-sm font-bold text-green-700">
                You already have access to this course
              </button>
            ) : card?.planId != null && card.priceFormatted ? (
              <Link
                to={`/checkout/${card.planId}`}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3 text-sm font-bold text-white shadow-md transition hover:bg-indigo-700 hover:shadow-lg active:scale-[0.98]"
              >
                Get started <ArrowRight className="h-4 w-4" />
              </Link>
            ) : (
              <button disabled className="w-full cursor-not-allowed rounded-xl bg-gray-100 py-3 text-sm font-bold text-gray-400">
                {cards == null ? "Loading…" : "Not available"}
              </button>
            )}

            <p className="mt-3 text-center text-xs text-gray-400">Encrypted, secure checkout</p>
          </div>
        ))}
      </div>

      {/* Lifetime nudge */}
      <p className="mb-16 text-center text-sm text-gray-500">
        Looking for a different course?{" "}
        <Link to="/courses" className="font-semibold text-primary underline underline-offset-2 hover:text-indigo-700">
          Browse courses
        </Link>{" "}
        and pick the level you want.
      </p>

      {/* FAQ */}
      <div className="mx-auto max-w-2xl">
        <div className="mb-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900">Frequently asked questions</h2>
        </div>

        <div className="space-y-3">
          {FAQ.map((item, i) => {
            const isOpen = openFaq === i;
            return (
              <div
                key={i}
                className={`rounded-2xl border transition-all duration-200 ${
                  isOpen
                    ? "border-indigo-200 bg-white shadow-md"
                    : "border-gray-200 bg-white hover:border-indigo-100 hover:shadow-sm"
                }`}
              >
                <button
                  type="button"
                  onClick={() => setOpenFaq(isOpen ? null : i)}
                  className="flex w-full items-center justify-between px-6 py-5 text-left"
                >
                  <span className={`text-sm font-semibold transition-colors ${isOpen ? "text-primary" : "text-gray-800"}`}>
                    {item.q}
                  </span>
                  <span className={`ml-4 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition-all duration-200 ${
                    isOpen
                      ? "border-indigo-300 bg-indigo-50 text-primary rotate-45"
                      : "border-gray-200 bg-gray-50 text-gray-400"
                  } text-base font-light`}>
                    +
                  </span>
                </button>
                {isOpen && (
                  <p className="px-6 pb-6 text-sm leading-relaxed text-gray-500 border-t border-indigo-50 pt-4 mt-1">
                    {item.a}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
